import { CheckCircle, XCircle, Info, X } from 'lucide-react'
import type { Toast as ToastData, ToastType } from '../../hooks/useToast'

interface ToastContainerProps {
  toasts: ToastData[]
  onDismiss: (id: number) => void
}

const ICON_MAP: Record<ToastType, typeof CheckCircle> = {
  success: CheckCircle,
  error: XCircle,
  info: Info,
}

const STYLE_MAP: Record<ToastType, { border: string; icon: string; iconBg: string; bg: string }> = {
  success: {
    border: 'border-l-success-500',
    icon: 'text-success-600 dark:text-success-400',
    iconBg: 'bg-success-50 dark:bg-success-900/30',
    bg: 'bg-white dark:bg-neutral-800',
  },
  error: {
    border: 'border-l-error-500',
    icon: 'text-error-600 dark:text-error-400',
    iconBg: 'bg-error-50 dark:bg-error-900/30',
    bg: 'bg-white dark:bg-neutral-800',
  },
  info: {
    border: 'border-l-info-500',
    icon: 'text-info-600 dark:text-info-400',
    iconBg: 'bg-info-50 dark:bg-info-900/30',
    bg: 'bg-white dark:bg-neutral-800',
  },
}

const PROGRESS_COLOR: Record<ToastType, string> = {
  success: 'bg-success-500',
  error: 'bg-error-500',
  info: 'bg-info-500',
}

export function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  if (toasts.length === 0) return null

  return (
    <div className="fixed bottom-14 left-2 right-2 z-50 flex flex-col gap-1.5 pointer-events-none" role="region" aria-label="Notifications">
      {toasts.map((toast) => {
        const Icon = ICON_MAP[toast.type]
        const style = STYLE_MAP[toast.type]

        return (
          <div
            key={toast.id}
            role="alert"
            aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
            className={`pointer-events-auto relative flex items-center gap-2.5 px-3 py-2.5 rounded-xl shadow-xl border-l-[3px] ${style.border} ${style.bg} ${
              toast.dismissing ? 'animate-toast-out' : 'animate-toast-in'
            }`}
          >
            {/* Icon with colored circle background */}
            <div className={`toast-icon-circle ${style.iconBg}`}>
              <Icon size={13} className={style.icon} />
            </div>
            <span className="flex-1 text-xs font-medium text-neutral-700 dark:text-neutral-200">
              {toast.message}
            </span>
            {toast.action && (
              <button
                onClick={() => {
                  toast.action?.onAction()
                  onDismiss(toast.id)
                }}
                className={`shrink-0 text-[11px] font-semibold ${style.icon} hover:underline transition-colors`}
              >
                {toast.action.label}
              </button>
            )}
            <button
              onClick={() => onDismiss(toast.id)}
              className="shrink-0 p-0.5 rounded hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
              aria-label="Dismiss"
            >
              <X size={10} className="text-neutral-400" />
            </button>
            {/* Progress bar for auto-dismiss */}
            {!toast.dismissing && (
              <div className="absolute bottom-0 left-0 right-0 h-[2px] rounded-b-xl overflow-hidden">
                <div
                  className={`h-full ${PROGRESS_COLOR[toast.type]}`}
                  style={{ animation: `toast-progress ${toast.duration}ms linear forwards` }}
                />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
